import type { AnalyticsSource } from "../analytics.js";
import type { TeletypeAnimationConfig } from "./output-buffer.js";
import type { AppConfig, BootScript, BootStep, BootStyleName, BootTime } from "../types.js";

export interface BootContext {
    boot: BootScript;
    config: AppConfig;
}

export interface BootHandlers {
    appendBanner: (text: string) => void;
    appendLine: (text: string, style: Record<string, unknown>, instant?: boolean) => void;
    setAnimation: (animation: TeletypeAnimationConfig) => void;
    runCommand: (command: string, source: AnalyticsSource) => void;
    enableInput: () => void;
    playSound: (key: string, volume?: number) => void;
    tryPlayBoot: (callbacks: {
        onBootStart?: () => void;
        onBootEnd?: () => void;
        onLoopStart: () => void;
    }) => void;
    ensureLoop: () => void;
    getBootDurationMs: () => number;
    schedule: (delayMs: number, fn: () => void) => void;
    resolveStyle: (name?: BootStyleName) => Record<string, unknown>;
    isInputEnabled: () => boolean;
}

function getByPath(root: Record<string, unknown>, path: string): unknown {
    return path.split(".").reduce<unknown>((value, key) => {
        if (value == null || typeof value !== "object") return undefined;
        return (value as Record<string, unknown>)[key];
    }, root);
}

function interpolateTemplates(text: string, context: BootContext): string {
    const sources: Record<string, object> = {
        boot: context.boot,
        config: context.config,
    };
    return text.replace(/\{\{(\w+)\.(\w+)\}\}/g, (_match, scope, key) => {
        const value = (sources[scope] as Record<string, unknown> | undefined)?.[key];
        return value == null ? "" : String(value);
    });
}

function resolveStepText(step: BootStep, context: BootContext): string {
    if ("text" in step && step.text != null) {
        return interpolateTemplates(step.text, context);
    }

    if ("from" in step && step.from) {
        const value = getByPath(
            { boot: context.boot, config: context.config },
            step.from,
        );
        return value == null ? "" : String(value);
    }

    return "";
}

function parseBootTime(at: BootTime): number | "event" {
    if (typeof at === "number") {
        return at;
    }

    if (at.startsWith("on:")) {
        return "event";
    }

    throw new Error(`Unknown boot timing: ${at}`);
}

function eventName(at: BootTime): string | null {
    if (typeof at === "string" && at.startsWith("on:")) {
        return at.slice(3);
    }
    return null;
}

export class BootSequenceRunner {
    private generation = 0;
    private completed = false;

    reset(): void {
        this.generation += 1;
        this.completed = false;
    }

    run(script: BootScript, context: BootContext, handlers: BootHandlers): void {
        this.generation += 1;
        const generation = this.generation;
        const bootDurationMs = handlers.getBootDurationMs();
        const eventSteps = new Map<string, BootStep[]>();

        const guard = (fn: () => void): void => {
            if (generation !== this.generation) return;
            fn();
        };

        const executeStep = (step: BootStep): void => {
            guard(() => this.runStep(step, context, handlers));
        };

        const registerEventStep = (name: string, step: BootStep): void => {
            const list = eventSteps.get(name) ?? [];
            list.push(step);
            eventSteps.set(name, list);
        };

        const fireEvent = (name: string): void => {
            for (const step of eventSteps.get(name) ?? []) {
                executeStep(step);
            }
        };

        const runOnComplete = (): void => {
            guard(() => {
                if (this.completed) return;
                this.completed = true;

                for (const action of script.onComplete ?? []) {
                    if (action.action === "command") {
                        handlers.runCommand(action.value, "boot");
                    } else if (action.action === "enableInput") {
                        handlers.enableInput();
                    }
                }
            });
        };

        for (const step of script.steps) {
            const timing = parseBootTime(step.at);
            const event = eventName(step.at);

            if (timing === "event" && event) {
                registerEventStep(event, step);
                continue;
            }

            if (typeof timing === "number") {
                handlers.schedule(timing, () => executeStep(step));
            }
        }

        const completeOn = script.audio?.completeOn ?? "loop";

        handlers.tryPlayBoot({
            onBootStart: () => fireEvent("boot"),
            onBootEnd: () => {
                fireEvent("bootEnd");
                if (completeOn === "bootEnd") {
                    runOnComplete();
                }
            },
            onLoopStart: () => {
                fireEvent("loop");
                if (completeOn === "loop") {
                    runOnComplete();
                }
            },
        });

        const fallbackMs = script.audio?.fallbackLoopAfterBootMs;
        if (fallbackMs != null) {
            handlers.schedule(bootDurationMs + fallbackMs, () => {
                guard(() => {
                    if (!handlers.isInputEnabled()) {
                        handlers.ensureLoop();
                    }
                });
            });
        }
    }

    private runStep(step: BootStep, context: BootContext, handlers: BootHandlers): void {
        switch (step.action) {
            case "banner":
                handlers.appendBanner(resolveStepText(step, context));
                return;
            case "line":
                if (step.animation) {
                    handlers.setAnimation(step.animation);
                }
                handlers.appendLine(
                    resolveStepText(step, context),
                    handlers.resolveStyle(step.style),
                    step.instant,
                );
                return;
            case "sound":
                handlers.playSound(step.sound, step.volume);
                return;
            case "command":
                handlers.runCommand(step.value, "boot");
                return;
            case "enableInput":
                handlers.enableInput();
                return;
            case "ensureLoop":
                handlers.ensureLoop();
                return;
            case "setAnimation":
                handlers.setAnimation(step.animation);
                return;
        }
    }
}
